import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Mixpanel } from '../../../services/Mixpanel';

export const getTutorialJumpBackToStepId = (type) => {
  return `tutorial_jump_back_to_step_${type}`;
};

// scroll events don't bubble, so listening on window alone misses scrolling
// inside a nested container (e.g. Unlimited's own overflow-y-auto wrapper) —
// walk up and find any scrollable ancestors to listen on too.
const getScrollableAncestors = (element) => {
  const ancestors = [];
  let node = element?.parentElement;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      ancestors.push(node);
    }
    node = node.parentElement;
  }
  return ancestors;
};

// Card geometry, shared by the position math and the render so the two can't
// drift. CARD_WIDTH is the rendered `w-72`; the height is an estimate, since the
// card's copy is variable-length.
const CARD_WIDTH = 288; // w-72
const CARD_HEIGHT_ESTIMATE = 300;
const VIEWPORT_PADDING = 16;

// Keep a viewport offset far enough from both edges that the card stays fully
// on-screen. The far edge depends on the axis, so callers pass their own.
const clampVertical = (value, maxOffset) =>
  value === undefined ? undefined : Math.min(Math.max(value, VIEWPORT_PADDING), maxOffset);

const maxVerticalOffset = () =>
  Math.max(VIEWPORT_PADDING, window.innerHeight - CARD_HEIGHT_ESTIMATE - VIEWPORT_PADDING);

const clampLeft = (x) =>
  Math.min(
    Math.max(x, VIEWPORT_PADDING),
    Math.max(VIEWPORT_PADDING, window.innerWidth - CARD_WIDTH - VIEWPORT_PADDING)
  );

// Is the target far enough from both viewport edges that the callout will fit
// beside it without scrolling? Padding leaves room for the card itself.
const isComfortablyInViewport = (element) => {
  const padding = 100;
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  return rect.top >= padding && rect.bottom + padding <= windowHeight;
};

export const TutorialContext = createContext({
  currentStep: 0,
  steps: [],
  type: '',
  showTutorial: () => {},
  hideTutorial: () => {},
  nextStep: () => {},
  previousStep: () => {},
  position: null,
});

const TutorialProvider = ({ children }) => {
  const [tutorialState, setTutorialState] = useState({
    currentStep: 0,
    steps: [],
    type: '',
  });
  const [position, setPosition] = useState(null);
  const [justOpened, setJustOpened] = useState(false);
  // The path the open tour was shown on, so a dismissOnNavigate callout knows
  // when the teacher has actually left that page.
  const openedOnPath = useRef(null);
  const { pathname } = useLocation();
  const [jumpBackIntoTourState, setJumpBackIntoTourState] = useState([]); // {[type: string, step: number]}

  // autoScroll brings a newly-shown target into view — only wanted when a
  // step first becomes active (showTutorial / a step transition), not on
  // every scroll/resize tick, where it would fight the user's own scrolling
  // (e.g. scrolling down to read a tall highlighted section).
  const updatePosition = (elementId, { autoScroll = false } = {}) => {
    if (!elementId) {
      setPosition(null);
      return;
    }

    const element = document.querySelector(`[data-tutorial-id="${elementId}"]`);
    if (element) {
      const rect = element.getBoundingClientRect();
      // A matching-but-unrendered target (display:none — e.g. the create-folder
      // "+" inside ProjectsSidebar's `md:block hidden` rail on a narrow viewport)
      // measures 0x0, which would anchor the card to the top-left corner pointing
      // at nothing. Treat it as not-found so the retry loop keeps waiting instead.
      if (!rect.width && !rect.height) return false;

      const computedStyle = window.getComputedStyle(element);
      const elementZIndex = parseInt(computedStyle.zIndex) || 0;
      const windowHeight = window.innerHeight;

      // Calculate space needed for the tutorial card plus padding
      const TUTORIAL_CARD_HEIGHT = 300; // Approximate height of the tutorial card
      const PADDING = 20; // Extra padding
      const SPACE_NEEDED = TUTORIAL_CARD_HEIGHT + PADDING;

      // Check if element is too close to bottom
      let isNearBottom = rect.bottom > windowHeight - SPACE_NEEDED;
      let isNearTop = rect.top < 0;

      if (autoScroll && (isNearBottom || isNearTop)) {
        // Calculate how much we need to scroll to fit both the element and the tutorial card
        let targetPosition; // Space for element + card
        if (isNearTop) {
          targetPosition = windowHeight - SPACE_NEEDED - rect.height;
        } else {
          targetPosition = windowHeight - SPACE_NEEDED - rect.height;
        }
        const scrollAmount = Math.ceil(rect.top - targetPosition);
        const originalScrollY = window.scrollY;

        // Attempt to scroll immediately
        window.scrollTo({
          top: window.scrollY + scrollAmount,
          behavior: 'instant',
        });

        // Get new position after scroll
        const newRect = element.getBoundingClientRect();

        // Check if we have enough space now
        isNearBottom = newRect.bottom > windowHeight - SPACE_NEEDED;

        if (isNearBottom) {
          // if we're still near bottom, scroll back up to the original position (we'll just put the card at the bottom of the screen)
          window.scrollTo({
            top: originalScrollY,
            behavior: 'instant',
          });
        }
      }

      // Clamp vertically — without auto-scroll fighting the user (see above),
      // the tracked element can end up scrolled well above or below the viewport
      // while its step is still active (e.g. scrolling down to read a tall
      // highlighted section). Without this, `top`/`bottom` below can push the
      // card fully off-screen. Horizontal clamping lives in the render, which
      // derives the card's `left` from the live rect (see clampLeft).
      const maxOffset = maxVerticalOffset();

      setPosition({
        // Only set top if not near bottom
        top: clampVertical(isNearBottom ? undefined : rect.bottom, maxOffset),
        // When near bottom, anchor just above the element's top edge (not a flat
        // viewport-bottom offset) so the card doesn't overlap the element itself.
        bottom: clampVertical(isNearBottom ? windowHeight - rect.top + 12 : undefined, maxOffset),
        zIndex: elementZIndex + 1,
        isNearBottom,
      });
      return element;
    }
    return false;
  };

  // Add/remove highlight class from previous and current elements
  const updateHighlight = (prevStepId, currentStepId) => {
    if (prevStepId) {
      const prevElement = document.querySelector(`[data-tutorial-id="${prevStepId}"]`);
      if (prevElement) {
        prevElement.classList.remove('tutorial-target');
      }
    }

    if (currentStepId) {
      const currentElement = document.querySelector(`[data-tutorial-id="${currentStepId}"]`);
      if (currentElement) {
        currentElement.classList.add('tutorial-target');
      }
    }
  };

  // dismissOnNavigate: for a one-shot callout that only makes sense on the page it
  // opened on (e.g. the post-onboarding profile tip). Off by default — a tour whose
  // steps span pages (BTS Explore) has to survive its own navigation.
  const showTutorial = ({ currentStep = 0, steps = [], type, dismissOnNavigate = false }) => {
    const prevStepId = tutorialState.steps[tutorialState.currentStep]?.targetElementTutorialId;
    openedOnPath.current = window.location.pathname;
    setTutorialState({ currentStep, steps, type, dismissOnNavigate });
    setJustOpened(true);
    setJumpBackIntoTourState([]);

    // Get current step info
    const currentStepData = steps[currentStep];
    if (!currentStepData) return;

    // Update highlight
    updateHighlight(prevStepId, currentStepData.targetElementTutorialId);
    // Let the step prepare its own target (e.g. open a menu it lives inside)
    // before we try to find/position it below.
    currentStepData.onEnter?.();

    // Try to update position immediately
    const found = updatePosition(currentStepData.targetElementTutorialId, { autoScroll: true });
    if (!found) return;

    // If the step pins a scroll alignment (scrollIntoViewBlock), always scroll
    // to it — needed for targets in a nested scroll container (e.g. the prompt
    // library), where the autoScroll path above uses window.scrollTo and can't
    // move them. Otherwise only center when the target isn't comfortably in view.
    const scrollBlock = currentStepData.scrollIntoViewBlock;
    if (scrollBlock || !isComfortablyInViewport(found)) {
      found.scrollIntoView({
        behavior: 'smooth',
        block: scrollBlock || 'center',
        inline: 'nearest',
      });
    }
  };

  const nextStep = () => {
    Mixpanel.track(`${tutorialState.type} Next Step Clicked`, {
      from: tutorialState.steps[tutorialState.currentStep]?.targetElementTutorialId,
      to: tutorialState.steps[tutorialState.currentStep + 1]?.targetElementTutorialId || 'done',
    });
    if (tutorialState.currentStep < tutorialState.steps.length - 1) {
      const prevStepId = tutorialState.steps[tutorialState.currentStep]?.targetElementTutorialId;
      const nextStepId =
        tutorialState.steps[tutorialState.currentStep + 1]?.targetElementTutorialId;

      setTutorialState((prev) => ({
        ...prev,
        currentStep: prev.currentStep + 1,
      }));

      updateHighlight(prevStepId, nextStepId);
      tutorialState.steps[tutorialState.currentStep + 1]?.onEnter?.();
    } else {
      hideTutorial();
    }
  };

  const previousStep = () => {
    Mixpanel.track(`${tutorialState.type} Previous Step Clicked`, {
      from: tutorialState.steps[tutorialState.currentStep]?.targetElementTutorialId,
      to: tutorialState.steps[tutorialState.currentStep - 1]?.targetElementTutorialId || 'done',
    });
    if (tutorialState.currentStep > 0) {
      const prevStepId = tutorialState.steps[tutorialState.currentStep]?.targetElementTutorialId;
      const nextStepId =
        tutorialState.steps[tutorialState.currentStep - 1]?.targetElementTutorialId;

      setTutorialState((prev) => ({
        ...prev,
        currentStep: prev.currentStep - 1,
      }));

      updateHighlight(prevStepId, nextStepId);
      tutorialState.steps[tutorialState.currentStep - 1]?.onEnter?.();
    } else {
      hideTutorial();
    }
  };

  // closeContext: the Mixpanel props for why this close happened (e.g.
  // `{ location: 'submit' }`). Passing it emits the same '<type> Closed' event the
  // outside-click listener below sends, so a caller that knows why it is closing
  // doesn't have to assemble the event name itself. Omit it for a silent close.
  //
  // Memoized because this provider re-renders on every scroll tick while a callout
  // is open (updatePosition -> setPosition) and consumers put hideTutorial in their
  // own dependency arrays (see Unlimited's useSubmitFlow). The type is the only
  // reactive value it reads, and that changes only when a tour opens or closes.
  const hideTutorial = useCallback(
    (closeContext) => {
      // No type means nothing is open — a caller closing unconditionally (the
      // workshop submit does) must not emit a ' Closed' event with no tour. Keying
      // on `location` also keeps a stray `onClick={hideTutorial}` from posting a
      // React event object as the props.
      if (closeContext?.location && tutorialState.type) {
        Mixpanel.track(`${tutorialState.type} Closed`, closeContext);
      }

      // get all elements that have data-tutorial-id tag
      const elements = document.querySelectorAll('[data-tutorial-id]');
      elements.forEach((element) => {
        element.classList.remove('tutorial-target');
      });

      // remove the last item from jumpBackIntoTourState
      openedOnPath.current = null;
      setTutorialState({ currentStep: 0, steps: [] });
      setPosition(null);
    },
    [tutorialState.type]
  );

  // A dismissOnNavigate callout points at something on one page, so leaving that
  // page has to close it — the outside-click listener below only fires on a
  // mousedown, which misses every keyboard-driven or programmatic navigation.
  useEffect(() => {
    if (!tutorialState.dismissOnNavigate || tutorialState.steps.length === 0) return;
    if (!openedOnPath.current || openedOnPath.current === pathname) return;
    hideTutorial({ location: 'navigation' });
  }, [pathname]);

  // useEffect to close tutorial when clicking outside of it.
  //
  // tutorialState has to be a dependency, not justOpened alone: closing the tour
  // changes only tutorialState, so without it this effect never re-runs, React never
  // runs the cleanup, and the listener stays attached holding the closed tour's
  // state. Every later mousedown then re-fired '<type> Closed' — ~20 per user per
  // day in prod — and read a stale type/currentStep.
  useEffect(() => {
    if (tutorialState.steps.length === 0) return;

    const handleClickOutside = (event) => {
      if (justOpened) return;

      // Dragging the scrollbar itself (e.g. to get back up and see what a
      // step is pointing at) is a mousedown on the page too, but it isn't
      // "clicking away" from the tour — don't close it for that. A native
      // scrollbar occupies the gutter beyond the document's clientWidth.
      if (event.clientX >= document.documentElement.clientWidth) return;

      const isClickingTutorialCard = event.target.closest('#tutorial-card');

      // Lets a control elsewhere on the page (e.g. the BTS sidebar tip card's
      // own "Next tip" button) manage the tour itself — advance/reopen it via
      // showTutorial — without this listener closing it first. Without this,
      // the mousedown here fires (and closes the tour) before that control's
      // own click handler runs, so its "is a tour open?" check always sees it
      // as already closed.
      const isClickingIgnoredElement = event.target.closest('[data-tutorial-ignore-outside-click]');

      const didClickRelatedButton = event.target.closest('[data-tutorial-id]');

      if (isClickingTutorialCard || isClickingIgnoredElement) {
        return;
      }

      Mixpanel.track(`${tutorialState.type} Closed`, {
        location: 'outside_click',
        did_click_tutorial_button: didClickRelatedButton,
      });

      if (didClickRelatedButton) {
        // set session state to jump back into the tutorial
        setJumpBackIntoTourState((prev) => [
          ...prev,
          { type: tutorialState.type, step: tutorialState.currentStep },
        ]);
      }
      hideTutorial();
    };

    setJustOpened(false);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [justOpened, tutorialState]);

  // Update position on scroll and resize
  useEffect(() => {
    const currentStep = tutorialState.steps[tutorialState.currentStep];
    if (currentStep?.targetElementTutorialId) {
      // Plain tracking on scroll/resize — no auto-scroll here, or it would
      // fight the user's own scrolling (e.g. scrolling down to read a tall
      // highlighted section) every time it fires.
      const handleUpdate = () => updatePosition(currentStep.targetElementTutorialId);
      window.addEventListener('scroll', handleUpdate);
      window.addEventListener('resize', handleUpdate);

      // The target may live inside a nested scrollable container (scroll
      // events don't bubble to window), and may not even be mounted yet if
      // this step's onEnter just opened a menu it lives inside — so keep
      // trying to find it and attach to its real scroll parent(s) until it
      // shows up, instead of only checking once up front.
      let attachedScrollParents = [];
      const attachScrollParents = () => {
        if (attachedScrollParents.length) return;
        const targetElement = document.querySelector(
          `[data-tutorial-id="${currentStep.targetElementTutorialId}"]`
        );
        attachedScrollParents = getScrollableAncestors(targetElement);
        attachedScrollParents.forEach((parent) => parent.addEventListener('scroll', handleUpdate));
      };
      attachScrollParents();

      // Retry briefly in case the target needs a moment to mount. No autoScroll
      // here: showTutorial already scrolled to the target it opened on, and its
      // instant window.scrollTo would fight the smooth scrollIntoView still in
      // flight — a target that mounts late gets a single scrollIntoView below.
      let attempts = 0;
      const retry = setInterval(() => {
        attempts += 1;
        attachScrollParents();
        const found = updatePosition(currentStep.targetElementTutorialId);
        if (found) {
          // A step that navigates to a new page (onEnter) mounts its target
          // after updateHighlight ran on the transition, so the highlight glow
          // was applied to a not-yet-existent element. Re-apply it here once the
          // element is really in the DOM.
          found.classList.add('tutorial-target');
          // Bring a late-mounting target into view. scrollIntoView (unlike the
          // window.scrollTo in updatePosition's autoScroll path) also works for
          // targets inside a nested scroll container. Steps can pin the
          // alignment via scrollIntoViewBlock; otherwise only scroll when the
          // target isn't already comfortably in view, so we don't yank the page
          // out from under a user who can already see it.
          if (currentStep.scrollIntoViewBlock || !isComfortablyInViewport(found)) {
            found.scrollIntoView({
              behavior: 'smooth',
              block: currentStep.scrollIntoViewBlock || 'center',
              inline: 'nearest',
            });
          }
          clearInterval(retry);
        } else if (attempts > 20) {
          clearInterval(retry);
        }
      }, 100);

      return () => {
        clearInterval(retry);
        window.removeEventListener('scroll', handleUpdate);
        window.removeEventListener('resize', handleUpdate);
        attachedScrollParents.forEach((parent) =>
          parent.removeEventListener('scroll', handleUpdate)
        );
      };
    }
  }, [tutorialState.currentStep, tutorialState.steps]);

  return (
    <TutorialContext.Provider
      value={{
        currentStep: tutorialState.currentStep,
        steps: tutorialState.steps,
        type: tutorialState.type,
        showTutorial,
        hideTutorial,
        nextStep,
        previousStep,
        jumpBackIntoTourState,
      }}>
      {children}
      {tutorialState.steps.length > 0 &&
        position &&
        (() => {
          const currentStep = tutorialState.steps[tutorialState.currentStep];
          if (!currentStep) return null;

          // Get the current position of the target element
          const element = document.querySelector(
            `[data-tutorial-id="${currentStep.targetElementTutorialId}"]`
          );
          if (!element) return null;

          const rect = element.getBoundingClientRect();
          // Same "is it actually rendered?" test updatePosition applies. Without
          // it, a display:none target measures 0x0 and the card would draw at the
          // viewport's left edge against a `position` left over from the previous
          // step, pointing at nothing.
          if (!rect.width && !rect.height) return null;

          // placement 'top' sits the card above the target, horizontally centered
          // over it, with the arrow on the card's bottom edge pointing down.
          // Otherwise the card falls back to the geometry-driven above/below
          // placement.
          const placeTop = currentStep.placement === 'top';
          const anchoredAbove = placeTop || position.isNearBottom;
          // Default (below/above, non-centered) placement: the arrow is inset a bit
          // from the card's left edge — pointing at the target's center, or
          // (alignLeft) with the card left-aligned to the target's left edge.
          const ARROW_INSET = 24;
          const alignLeft = currentStep.alignLeft;
          const cardLeft = placeTop
            ? // centered over the target; offset baked into `left` (not a
              // translateX) so the fade-in's transform animation can't clobber it
              clampLeft(rect.left + rect.width / 2 - CARD_WIDTH / 2)
            : alignLeft
              ? clampLeft(rect.left)
              : clampLeft(rect.left + rect.width / 2 - ARROW_INSET);

          return (
            <div
              id='tutorial-card'
              // No position transition — during a smooth scroll the card's
              // top/left update every frame, and a CSS transition would ease
              // toward each new value and never catch up, making it jiggle. The
              // card instead tracks the target 1:1, which reads as smooth.
              className='w-72 px-4 py-2 animate-fade-in border border-gray-200'
              style={{
                position: 'fixed',
                ...(placeTop
                  ? {
                      // Above the target, clamped the same way updatePosition
                      // clamps the geometry-driven anchors so a target near the
                      // top of the viewport can't push the card off the top edge.
                      bottom: clampVertical(
                        window.innerHeight - rect.top + 10,
                        maxVerticalOffset()
                      ),
                    }
                  : position.isNearBottom
                    ? { bottom: position.bottom } // Anchored just above the element
                    : { top: position.top + 10 }),
                left: cardLeft,
                transform: currentStep.direction === 'right' ? 'translateX(-60%)' : undefined,
                zIndex: position.zIndex + 1000,
                background: 'white',
                // Near-centered vertically (small y-offset + a symmetric ambient
                // layer) so the shadow wraps the top edge too, giving the
                // up-pointing arrow protrusions some depth instead of only the
                // bottom edge getting a shadow.
                boxShadow: '0 2px 20px rgba(0, 0, 0, 0.12), 0 0 10px rgba(0, 0, 0, 0.08)',
                borderRadius: '12px',
              }}>
              {/* arrow — points down when the card sits above the element,
                  otherwise points up. No box-shadow on purpose: a plain white nub
                  reads as part of the card, and the card's own soft shadow avoids
                  the double-shadow the arrow's directional shadow used to create. */}
              <div
                className={`absolute w-3 h-3 bg-white transform rotate-45 ${
                  anchoredAbove ? '-bottom-1.5 translate-y-1/2' : '-top-1.5 -translate-y-1/2'
                }`}
                style={{
                  left: alignLeft
                    ? `${ARROW_INSET}px` // fixed inset from the (left-aligned) card's left edge
                    : currentStep.direction === 'right'
                      ? `calc(100% - ${rect.width / 2}px)` // Center of the target element when right-aligned
                      : // Offset from the card's own left edge to the target's horizontal
                        // center. For placeTop (card centered over the target) this
                        // resolves to ~50%; if cardLeft was clamped near a viewport edge
                        // the arrow self-corrects instead of drifting off the target.
                        // Clamped to stay within the card itself.
                        `${Math.min(Math.max(rect.left + rect.width / 2 - cardLeft, 12), CARD_WIDTH - 12)}px`,
                  transform: 'translateX(-50%) rotate(45deg)',
                  // outline only the two outward-facing edges of the nub (down
                  // when the card is above the target, up otherwise) so it picks
                  // up the card's gray-100 border with no seam at the base
                  ...(anchoredAbove
                    ? { borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }
                    : { borderTop: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb' }),
                }}></div>
              {/* These callouts don't navigate — each tip is a standalone
                  pointer. Just the text with a close button on the far right. */}
              <div className='relative flex items-start gap-2'>
                <div className='min-w-0 flex-1 py-0.5 text-sm'>
                  {currentStep.header && (
                    <p className='font-medium text-gray-900'>{currentStep.header}</p>
                  )}
                  {currentStep.body && <p className='text-gray-600 text-xs'>{currentStep.body}</p>}
                  {currentStep.learnMoreLink && (
                    <p className='my-0.5 text-xs '>
                      <a
                        onClick={() => {
                          Mixpanel.track(`${tutorialState.type} Learn More Clicked`, {
                            for: tutorialState.steps[tutorialState.currentStep]
                              ?.targetElementTutorialId,
                            link: currentStep.learnMoreLink,
                          });
                        }}
                        href={currentStep.learnMoreLink}
                        target='_blank'
                        className='h-fit hover:text-emerald-600 text-gray-600 flex items-center gap-x-0.5'
                        rel='noopener noreferrer'>
                        Learn more
                        <span className='mt-[2px]'>
                          <ChevronRightIcon className='w-3 h-3' />
                        </span>
                      </a>
                    </p>
                  )}
                </div>
                <button
                  aria-label='Close'
                  className='-mr-1 shrink-0 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  onClick={() => {
                    Mixpanel.track(`${tutorialState.type} Closed`, {
                      location: 'x_button',
                    });
                    hideTutorial();
                  }}>
                  <XMarkIcon className='w-4 h-4' />
                </button>
              </div>
            </div>
          );
        })()}
    </TutorialContext.Provider>
  );
};

export default TutorialProvider;

export const useTutorialContext = () => {
  return useContext(TutorialContext);
};
